import React, { useCallback, useEffect, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { venueImageUrl } from '../../api';
import {
  createEvent,
  deleteEvent,
  EVENT_CATEGORIES,
  EventInput,
  eventWhen,
  getMyEvents,
  MAX_EVENT_DESCRIPTION,
  PartnerEvent,
  removeEventPoster,
  saveEvent,
  uploadEventPoster
} from '../../features/events/api';
import { ACCEPTED_PHOTO_TYPES, errorMessage, Notice, NoticeBox, PageHead, photoProblem, StatusBadge, Toggle } from './shared';

type Form = Record<'title' | 'category' | 'description' | 'eventDate' | 'startTime' | 'endTime' | 'entryInfo', string> & { isPublished: boolean };

const EMPTY: Form = { title: '', category: EVENT_CATEGORIES[0], description: '', eventDate: '', startTime: '21:00', endTime: '', entryInfo: '', isPublished: true };

const toForm = (e: PartnerEvent): Form => ({
  title: e.title, category: e.category, description: e.description, eventDate: e.eventDate,
  startTime: e.startTime.slice(0, 5), endTime: e.endTime?.slice(0, 5) ?? '', entryInfo: e.entryInfo ?? '', isPublished: e.isPublished
});

const apiTime = (t: string) => (t.length === 5 ? `${t}:00` : t);
const toInput = (f: Form): EventInput => ({
  title: f.title, category: f.category, description: f.description, eventDate: f.eventDate,
  startTime: apiTime(f.startTime), endTime: f.endTime ? apiTime(f.endTime) : null, entryInfo: f.entryInfo || null, isPublished: f.isPublished
});

// Partner-facing names for EventService statuses.
const STATUS_LABEL: Record<string, string> = { Upcoming: 'Active', Today: 'Active', Past: 'Expired', Pending: 'In Review' };

function Editor({ event, onClose, onSaved }: { event: PartnerEvent | null; onClose: () => void; onSaved: (text: string) => void }) {
  const [form, setForm] = useState<Form>(event ? toForm(event) : EMPTY);
  const [poster, setPoster] = useState<File | null>(null);
  const [posterUrl, setPosterUrl] = useState<string | null>(venueImageUrl(event?.posterUrl) ?? null);
  const [removePoster, setRemovePoster] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [saving, setSaving] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const set = (key: keyof Form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm({ ...form, [key]: e.target.value });

  // Release the preview's blob URL.
  useEffect(() => () => { if (posterUrl?.startsWith('blob:')) URL.revokeObjectURL(posterUrl); }, [posterUrl]);

  const pickPoster = (file?: File) => {
    if (!file) return;
    const problem = photoProblem(file);
    if (problem) {
      setNotice({ severity: 'error', text: problem });
      return;
    }
    setPoster(file);
    setRemovePoster(false);
    setPosterUrl(URL.createObjectURL(file));
  };

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.description.length > MAX_EVENT_DESCRIPTION) {
      setNotice({ severity: 'error', text: `Keep the description under ${MAX_EVENT_DESCRIPTION} characters.` });
      return;
    }
    setSaving(true);
    setNotice(null);
    try {
      const saved = event ? await saveEvent(event.id, toInput(form)) : await createEvent(toInput(form));
      let posterNote = '';
      try {
        if (poster) await uploadEventPoster(saved.id, poster);
        else if (removePoster && event?.posterUrl) await removeEventPoster(saved.id);
      } catch (err) {
        posterNote = ` The poster wasn't saved: ${errorMessage(err, 'upload failed.')}`;
      }
      onSaved(saved.status === 'Draft' ? `"${saved.title}" saved as a draft.${posterNote}`
        : saved.status === 'Suspended' ? `"${saved.title}" saved. It stays hidden while suspended; contact support.${posterNote}`
          : saved.status === 'Pending' ? `"${saved.title}" saved and sent for review.${posterNote}`
            : `"${saved.title}" is live on Cheers2Night.${posterNote}`);
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Could not save the event.') });
      setSaving(false);
    }
  };

  return (
    <form className="pp-editor" onSubmit={save} aria-label={event ? 'Edit event' : 'New event'} style={{ marginBottom: 18 }}>
      <div className="pp-editor-head">
        <h3>{event ? 'Edit Event' : 'New Event'}</h3>
        <button type="button" className="pp-icon-btn" onClick={onClose} aria-label="Close"><CloseIcon /></button>
      </div>
      <div style={{ padding: notice ? '14px 20px 0' : 0 }}><NoticeBox notice={notice} /></div>
      <div className="pp-editor-cols">
        <div className="pp-editor-col">
          <div className="pp-field">
            <label htmlFor="event-title">Event Name<span className="pp-req">*</span></label>
            <input id="event-title" className="pp-input" maxLength={120} placeholder="Bollywood Night" value={form.title} onChange={set('title')} />
          </div>
          <div className="pp-field">
            <label htmlFor="event-category">Category<span className="pp-req">*</span></label>
            <select id="event-category" className="pp-input" value={form.category} onChange={set('category')}>{EVENT_CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
          </div>
          <div className="pp-field">
            <label htmlFor="event-description">Description<span className="pp-req">*</span></label>
            <textarea id="event-description" className="pp-input" style={{ minHeight: 120 }} value={form.description} onChange={set('description')}
              placeholder="DJ line-up, music, dress code, what to expect..." />
            <span className={`pp-counter ${form.description.length > MAX_EVENT_DESCRIPTION ? 'over' : ''}`}>{form.description.length}/{MAX_EVENT_DESCRIPTION}</span>
          </div>
          <div className="pp-field">
            <label htmlFor="event-entry">Entry Details</label>
            <input id="event-entry" className="pp-input" maxLength={120} placeholder="Free entry before 10 PM · ₹999 cover after" value={form.entryInfo} onChange={set('entryInfo')} />
          </div>
        </div>
        <div className="pp-editor-col">
          <div className="pp-field">
            <label htmlFor="event-date">Date<span className="pp-req">*</span></label>
            <input id="event-date" type="date" className="pp-input" value={form.eventDate} onChange={set('eventDate')} />
          </div>
          <div className="pp-row">
            <div className="pp-field">
              <label htmlFor="event-start">Start Time<span className="pp-req">*</span></label>
              <input id="event-start" type="time" className="pp-input" value={form.startTime} onChange={set('startTime')} />
            </div>
            <div className="pp-field">
              <label htmlFor="event-end">End Time</label>
              <input id="event-end" type="time" className="pp-input" value={form.endTime} onChange={set('endTime')} />
            </div>
          </div>
          <div>
            <span className="pp-label">Poster</span>
            <p className="pp-muted" style={{ margin: '-2px 0 8px', fontSize: 13 }}>Shown on the event page and in listings. Without one, your venue's cover photo is used.</p>
            {posterUrl && !removePoster ? (
              <div className="pp-thumbs"><div className="pp-thumb cover" style={{ width: 160, height: 200 }}>
                <img src={posterUrl} alt="Event poster" />
                <button type="button" className="pp-thumb-x" aria-label="Remove poster" onClick={() => { setPoster(null); setRemovePoster(true); }}><CloseIcon /></button>
              </div></div>
            ) : null}
            <button type="button" className="pp-btn pp-outline" onClick={() => fileInput.current?.click()}>{posterUrl && !removePoster ? 'Replace Poster' : 'Choose Poster'}</button>
            <span className="pp-muted" style={{ fontSize: 12, marginLeft: 8 }}>JPG, PNG or WebP · 5 MB</span>
            <input ref={fileInput} hidden type="file" accept={ACCEPTED_PHOTO_TYPES.join(',')} onChange={(e) => { pickPoster(e.target.files?.[0]); e.target.value = ''; }} />
          </div>
          <div className="pp-toggles">
            <Toggle checked={form.isPublished} onChange={(v) => setForm({ ...form, isPublished: v })} label="Published (visible on Cheers2Night)" />
          </div>
        </div>
      </div>
      <div className="pp-editor-foot">
        <button type="button" className="pp-btn pp-outline" onClick={onClose}>Cancel</button>
        <button type="submit" className="pp-btn pp-gold" disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
      </div>
    </form>
  );
}

// Partner portal → Events: list DJ nights, live music and other events at the venue. Published
// events show on the Events page, the venue page and in the State / District filters straight away.
const EventsPage: React.FC = () => {
  const [events, setEvents] = useState<PartnerEvent[] | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const [editing, setEditing] = useState<PartnerEvent | 'new' | null>(null);

  const load = useCallback(() => getMyEvents().then(setEvents).catch(() => setNotice({ severity: 'error', text: 'Could not load your events. Please refresh.' })), []);
  useEffect(() => {
    load();
  }, [load]);

  const remove = async (ev: PartnerEvent) => {
    if (!window.confirm(`Delete "${ev.title}"? Customers will no longer see it. This can't be undone.`)) return;
    try {
      await deleteEvent(ev.id);
      setNotice({ severity: 'success', text: `"${ev.title}" deleted.` });
      if (editing !== 'new' && editing?.id === ev.id) setEditing(null);
      load();
    } catch (err) {
      setNotice({ severity: 'error', text: errorMessage(err, 'Could not delete the event.') });
    }
  };

  const live = (ev: PartnerEvent) => ev.status === 'Upcoming' || ev.status === 'Today';

  return (
    <>
      <PageHead title="Events" subtitle="List DJ nights, live music and parties at your venue.">
        <button type="button" className="pp-btn pp-gold" onClick={() => setEditing('new')}><AddIcon />New Event</button>
      </PageHead>
      <NoticeBox notice={notice} />

      {editing !== null && (
        <Editor
          key={editing === 'new' ? 'new' : editing.id}
          event={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(text) => { setEditing(null); setNotice({ severity: 'success', text }); load(); }}
        />
      )}

      {events === null ? (
        <p className="pp-muted">Loading events...</p>
      ) : events.length === 0 ? (
        <div className="pp-panel pp-empty">
          <h3>No events yet</h3>
          <p className="pp-muted">Add your next DJ night or live gig. Published events appear on the Events page and your venue page right away.</p>
          <button type="button" className="pp-btn pp-gold" onClick={() => setEditing('new')}><AddIcon />New Event</button>
        </div>
      ) : (
        <section className="pp-panel">
          <div className="pp-table-wrap">
            <table className="pp-table">
              <thead><tr><th>Event</th><th>When</th><th>Status</th><th className="num">Actions</th></tr></thead>
              <tbody>
                {events.map((ev) => (
                  <tr key={ev.id}>
                    <td>
                      <b>{ev.title}</b>
                      <div className="pp-muted" style={{ fontSize: 12 }}>{ev.category}{ev.entryInfo ? ` · ${ev.entryInfo}` : ''}</div>
                      {ev.moderationNote && (ev.status === 'Rejected' || ev.status === 'Suspended') && (
                        <div className="pp-notice error" style={{ margin: '6px 0 0', padding: '6px 9px', fontSize: 12 }}>Review note: {ev.moderationNote}</div>
                      )}
                    </td>
                    <td>{eventWhen(ev)}</td>
                    <td><StatusBadge status={STATUS_LABEL[ev.status] ?? ev.status} /></td>
                    <td className="num" style={{ whiteSpace: 'nowrap' }}>
                      {live(ev) && <a className="pp-btn pp-outline pp-sm" href={`/events/${ev.id}`} target="_blank" rel="noreferrer">View</a>}{' '}
                      <button type="button" className="pp-btn pp-outline pp-sm" onClick={() => setEditing(ev)}>Edit</button>{' '}
                      <button type="button" className="pp-btn pp-outline pp-sm pp-danger" onClick={() => remove(ev)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="pp-hint">Past events stay here so you can copy them: edit one, pick a new date and save.</p>
        </section>
      )}
    </>
  );
};

export default EventsPage;
